// One-time native shell setup. Called from main.tsx on boot.
// Configures status bar, edge-to-edge layout, and hardware back button so
// the Capacitor APK feels like a true native app instead of a web wrapper.
import { isNativeApp, isAndroidApp } from "./platform";

const BRAND_BG = "#0F172A";

/**
 * Configure the native shell once on app boot. Safe to call on web — every
 * branch is gated by `isNativeApp()`.
 */
export async function initNativeShell(): Promise<void> {
  if (!isNativeApp()) return;

  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await StatusBar.setStyle({ style: Style.Dark });
    if (isAndroidApp()) {
      await StatusBar.setBackgroundColor({ color: BRAND_BG });
      await StatusBar.setOverlaysWebView({ overlay: true });
    }
  } catch {
    /* StatusBar plugin missing or unsupported — non-fatal */
  }
}

/**
 * Wire the Android hardware back button to React Router history instead of
 * letting the OS close the app from any screen. Called once from <App />.
 *
 * Behaviour (Android best practice):
 *   1. If a Radix-style overlay is open (dialog, sheet, popover, dropdown),
 *      close it first — never navigate behind it.
 *   2. Otherwise, walk router history if we can.
 *   3. On the root with nothing to pop, exit the app.
 *
 * Returns a cleanup function for React useEffect.
 */
export function registerAndroidBackButton(
  navigate: (delta: number) => void,
  exitOnRoot: boolean = true,
): () => void {
  if (!isAndroidApp()) return () => undefined;

  let listenerHandle: { remove: () => Promise<void> } | undefined;

  (async () => {
    try {
      const { App: CapApp } = await import("@capacitor/app");
      listenerHandle = await CapApp.addListener("backButton", ({ canGoBack }) => {
        // 1) Close any open Radix overlay first. Radix marks open dialogs/
        //    sheets/popovers/dropdowns with [data-state="open"] on a
        //    [role="dialog"] or [data-radix-popper-content-wrapper] element.
        const openOverlay = document.querySelector(
          '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]',
        );
        if (openOverlay) {
          // Prefer the close button if present, otherwise dispatch Escape.
          const closeBtn = openOverlay.querySelector<HTMLElement>(
            'button[aria-label="Close"], [data-radix-dialog-close]',
          );
          if (closeBtn) {
            closeBtn.click();
          } else {
            document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape" }));
          }
          return;
        }

        if (canGoBack) {
          navigate(-1);
        } else if (exitOnRoot) {
          CapApp.exitApp();
        }
      });
    } catch {
      /* @capacitor/app missing — non-fatal */
    }
  })();

  return () => {
    listenerHandle?.remove().catch(() => undefined);
  };
}
